import useAppFeatures from '@hooks/useAppFeatures';
import { useTranslate } from 'nucleus-text';
import useMeQuery from '@hooks/useMeQuery';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

const urlBase = publicRuntimeConfig.BASE_PATH;

/**
 *
 * @returns
 */
const useHomeTab = (hasPublicLandingPage, eventId) => {
  const { translate } = useTranslate();

  return {
    index: {
      label: translate('Navigation-Tab-MyEvent'),
      value: hasPublicLandingPage
        ? `/events/${eventId}/info`
        : `/events/${eventId}`,
      testID: 'navigation-tab-my-event',
      page: hasPublicLandingPage
        ? '/events/[eventId]/info'
        : '/events/[eventId]',
      fullUrl: hasPublicLandingPage
        ? `${urlBase}/events/${eventId}/info`
        : `${urlBase}/events/${eventId}`
    }
  };
};

// Before adding new tabs refer to ./docs/attendeeHubPages.md
const useTabDefinitions = hasPublicLandingPage => {
  const appFeatures = useAppFeatures();
  // Fetch event data to get event features
  const { data } = useMeQuery();
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();

  const { event } = data?.me || {};
  const eventId = event?.id;
  // show exhibitor list and sponsor list flags are controlled by content visibility settings
  const isSponsoredExhibitorsEnabled =
    event?.features?.sponsors &&
    event?.features?.showSponsorList &&
    isFeatureSettingEnabled('EXHIBITOR_LIST');
  const isExhibitorListVisible =
    event?.features?.exhibitorManagement &&
    event?.features?.showExhibitorList &&
    isFeatureSettingEnabled('EXHIBITOR_LIST');
  const exhibitorManagementIsEnabled = event?.features?.exhibitorManagement;
  const areExhibitorsPresent = event?.features?.exhibitorsPresent;
  const showAllSessionsTab = event?.features?.sessions;
  const showOnDemandTab = event?.features?.onDemandCatalog;
  const isGamePresent = event?.features?.gamePresent;
  const attendeeListEnabled = isFeatureSettingEnabled('ATTENDEE_LIST');
  const videoDiscussionsEnabled = isFeatureSettingEnabled('VIDEO_DISCUSSIONS');
  const isGamificationEnabled = isFeatureSettingEnabled('GAME');
  const speedNetworkingFeatureSetting =
    isFeatureSettingEnabled('SPEED_NETWORKING');
  const upcomingEventsEnabled = isFeatureSettingEnabled('UPCOMING_EVENTS');
  const speedNetworkingEnabled =
    speedNetworkingFeatureSetting && appFeatures.speedNetworking;
  const isConnectionsEnabled = isFeatureSettingEnabled('ATTENDEE_CONNECTIONS');

  const { translate } = useTranslate();
  let tabs = useHomeTab(hasPublicLandingPage, eventId);

  if (event) {
    if (showAllSessionsTab) {
      tabs = {
        ...tabs,
        allSessions: {
          label: translate('Navigation-Tab-AllSessions'),
          value: `/events/${eventId}/sessions`,
          testID: 'navigation-tab-all-sessions',
          page: '/events/[eventId]/sessions',
          fullUrl: `${urlBase}/events/${eventId}/sessions`
        }
      };
    }

    if (showOnDemandTab) {
      tabs = {
        ...tabs,
        onDemand: {
          label: translate('Navigation-Tab-OnDemand'),
          value: `/events/${eventId}/ondemand`,
          testID: 'navigation-tab-on-demand',
          page: '/events/[eventId]/ondemand',
          fullUrl: `${urlBase}/events/${eventId}/ondemand`
        }
      };
    }

    if (upcomingEventsEnabled) {
      tabs = {
        ...tabs,
        upcomingEvents: {
          label: translate('Navigation-Tab-Upcoming-Events'),
          value: `/events/${eventId}/upcomingevents`,
          testID: 'navigation-tab-upcoming-events',
          page: '/events/[eventId]/upcomingevents',
          fullUrl: `${urlBase}/events/${eventId}/upcomingevents`
        }
      };
    }

    if (appFeatures.stages) {
      tabs = {
        ...tabs,
        stages: {
          label: translate('Navigation-Tab-Stages'),
          value: `/events/${eventId}/stages`,
          testID: 'navigation-tab-stages',
          page: '/events/[eventId]/stages',
          fullUrl: `${urlBase}/events/${eventId}/stages`
        }
      };
    }

    // Check that exhibitor list can be visible and that the event has exhibitors
    if (isExhibitorListVisible && areExhibitorsPresent) {
      tabs = {
        ...tabs,
        exhibitors: {
          label: translate('Navigation-Tab-Exhibitors'),
          value: `/events/${eventId}/exhibitors`,
          testID: 'navigation-tab-exhibitors',
          page: '/events/[eventId]/exhibitors',
          fullUrl: `${urlBase}/events/${eventId}/exhibitors`
        }
      };
    }
    // Check that sponsor exhibitors are visible
    if (exhibitorManagementIsEnabled && isSponsoredExhibitorsEnabled) {
      tabs = {
        ...tabs,
        sponsors: {
          label: translate('Navigation-Tab-Sponsors'),
          value: `/events/${eventId}/sponsors`,
          testID: 'navigation-tab-sponsors',
          page: '/events/[eventId]/sponsors',
          fullUrl: `${urlBase}/events/${eventId}/sponsors`
        }
      };
    }

    if (attendeeListEnabled) {
      tabs = {
        ...tabs,
        attendeeList: {
          label: translate('Navigation-Tab-AttendeeList'),
          value: `/events/${eventId}/attendees`,
          testID: 'navigation-tab-attendee-list',
          page: '/events/[eventId]/attendees',
          fullUrl: `${urlBase}/events/${eventId}/attendees`
        }
      };
    }

    if (isGamificationEnabled && isGamePresent) {
      tabs = {
        ...tabs,
        gamification: {
          label: translate('Navigation-Tab-Gamification'),
          value: `/events/${eventId}/game`,
          testID: 'navigation-tab-gamification',
          page: '/events/[eventId]/game',
          fullUrl: `${urlBase}/events/${eventId}/game`
        }
      };
    }

    if (videoDiscussionsEnabled) {
      tabs = {
        ...tabs,
        videoDiscussions: {
          label: translate('Navigation-Tab-Discussions'),
          value: `/events/${eventId}/discussions`,
          testID: 'navigation-tab-video-discussions',
          page: '/events/[eventId]/discussions',
          fullUrl: `${urlBase}/events/${eventId}/discussions`
        }
      };
    }

    if (speedNetworkingEnabled) {
      tabs = {
        ...tabs,
        speedNetworking: {
          label: translate('Navigation-Tab-Speed-Networking'),
          value: `/events/${eventId}/speednetworking`,
          testID: 'navigation-speed-networking',
          page: '/events/[eventId]/speednetworking',
          fullUrl: `${urlBase}/events/${eventId}/speednetworking`
        }
      };
    }

    if (isConnectionsEnabled) {
      tabs = {
        ...tabs,
        connections: {
          label: translate('Navigation-Menu-Connections'),
          value: `/events/${eventId}/connections`,
          testID: 'navigation-connections',
          page: '/events/[eventId]/connections',
          fullUrl: `${urlBase}/events/${eventId}/connections`
        }
      };
    }
  }

  return tabs;
};

export default useTabDefinitions;
